import { useGetQuestionByIdQuery } from '@exam-management/graphql';
import { Navigate } from '@exam-management/router';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type { Question } from './types';

interface QContentProviderProps extends PropsWithChildren {
  contentId: string;
  structureId: string;
}

interface QContentContextData {
  qContent: Question;
}

const QContentContext = createContext<QContentContextData | undefined>(undefined);

function QContentProvider(props: Readonly<QContentProviderProps>) {
  const { contentId, children, structureId } = props;

  const { data: getQuestionResult, loading: isLoading } = useGetQuestionByIdQuery({
    variables: {
      id: contentId,
    },
  });

  const contextValue = useMemo(
    () => ({
      qContent: getQuestionResult?.qctGetQuestionById as Question,
    }),
    [getQuestionResult?.qctGetQuestionById],
  );

  if (isLoading) {
    return <Loading height='100%' />;
  }

  if (!getQuestionResult?.qctGetQuestionById) {
    return <Navigate to='/qbank-structures/:structureId/qcontents' params={{ structureId }} />;
  }

  return (
    <QContentContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </QContentContext.Provider>
  );
}

export { QContentContext, QContentProvider };
